// src/App.tsx - OPTIMIZADO
import React, { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { SideMenuProvider } from './contexts/SideMenuContext'
import Navbar from './components/Navbar'
import SideMenu from './components/SideMenu'
import WaveBackground from './components/WaveBackground'
import './App.css'

// Lazy loading para mejor performance
const Home = lazy(() => import('./pages/Home'))
const Bios = lazy(() => import('./pages/Bios'))
const News = lazy(() => import('./pages/News'))
const ReportProblem = lazy(() => import('./pages/ReportProblem'))
const About = lazy(() => import('./pages/About'))
const Legal = lazy(() => import('./pages/Legal'))
const ConsoleList = lazy(() => import('./pages/consoles/ConsoleList'))
const ConsolePage = lazy(() => import('./pages/consoles/ConsolePage'))

// Componente de carga
const LoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-slate-400 font-jetbrains">Cargando...</p>
    </div>
  </div>
)

// Error Boundary
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error capturado:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
          <div className="max-w-md w-full glass-effect rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">¡Oops! Algo salió mal</h1>
            <p className="text-slate-400 mb-6 font-jetbrains text-sm">
              {this.state.error?.message || 'Ha ocurrido un error inesperado'}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-jetbrains font-bold transition-colors duration-200"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <SideMenuProvider>
        <Router>
          <div className="min-h-screen bg-slate-900 text-white relative overflow-x-hidden">
            <WaveBackground />
            <Navbar />
            <SideMenu />
            
            <main>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/bios" element={<Bios />} />
                  <Route path="/news" element={<News />} />
                  <Route path="/report" element={<ReportProblem />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/legal" element={<Legal />} />
                  <Route path="/consoles" element={<ConsoleList />} />
                  <Route path="/consoles/:consoleName" element={<ConsolePage />} />
                  {/* Ruta 404 */}
                  <Route path="*" element={
                    <div className="min-h-screen pt-16 flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-6xl font-bold text-cyan-400 mb-4">404</h1>
                        <p className="text-slate-400 font-jetbrains mb-6">Página no encontrada</p>
                        <a href="/" className="btn-primary inline-block">
                          Volver al inicio
                        </a>
                      </div>
                    </div>
                  } />
                </Routes>
              </Suspense>
            </main>
          </div>
        </Router>
      </SideMenuProvider>
    </ErrorBoundary>
  )
}

export default App